import Entity from './Entity';

import InvoiceLineItem from './InvoiceLineItem';
import Payment from './Payment';
import Payor from './Payor';
import Parker from './Parker';
import Subscription from './Subscription';
import Refund from './Refund';
import Transaction from './Transaction';

import {
  queryUpdateInvoice,
} from '../Queries';


const invoiceEntityFieldsWithDynamicAllowedValues = {
  type: { enumName: 'InvoiceTypeEnum' },
  adjustmentType: { enumName: 'AdjustmentActionsTypeEnum' }
};

const invoiceEntityFieldsWithStaticAllowedValues = {
  parking_period_month: {
    options: [
      { value: 'Jan', label: 'January' },
      { value: 'Feb', label: 'February' },
      { value: 'Mar', label: 'March' },
      { value: 'Apr', label: 'April' },
      { value: 'May', label: 'May' },
      { value: 'Jun', label: 'June' },
      { value: 'Jul', label: 'July' },
      { value: 'Aug', label: 'August' },
      { value: 'Sep', label: 'September' },
      { value: 'Oct', label: 'October' },
      { value: 'Nov', label: 'November' },
      { value: 'Dec', label: 'December' },
    ]
  }
};

export const INVOICE_TYPE_ENUM = {
  CARRYOVER_INVOICE: 'CARRYOVER_INVOICE',
  MANUAL_INVOICE: 'MANUAL_INVOICE',
  FIRST_MONTH_INVOICE: 'FIRST_MONTH_INVOICE',
  AUTO_INVOICE: 'AUTO_INVOICE',
  VALIDATION_INVOICE: 'VALIDATION_INVOICE'
}

export const newInvoiceAllowedTypes = [
  INVOICE_TYPE_ENUM.CARRYOVER_INVOICE,
  INVOICE_TYPE_ENUM.MANUAL_INVOICE,
  INVOICE_TYPE_ENUM.VALIDATION_INVOICE
];

export default class Invoice {
  fields = {
    id: {
      value: null
    },
    subscription_id: {
      value: null
    },
    readable_id: {
      value: null
    },
    type: {
      value: null
    },
    due_on: {
      value: null
    },
    amount: {
      value: null
    },
    balance: {
      value: null
    },
    parking_period_month: {
      value: null
    },
    parking_period_year: {
      value: null
    },
    notes: {
      value: null
    },
    detail_notes: {
      value: null
    },
    payor: {
      value: null
    },
    parkers: {
      value: []
    },
    line_items: {
      value: []
    },
    subscription: {
      value: null
    },
    payments: {
      value: []
    },
    refunds: {
      value: []
    },
    transaction: {
      value: null
    },
    created_at: {
      value: null
    },
    
  };

  static createNewInvoice(type) {
    const invoice = new this();
    invoice.fields.type.value = INVOICE_TYPE_ENUM[type];

    return invoice;
  }

  get isCarryover() {
    return this.fields.type.value === INVOICE_TYPE_ENUM.CARRYOVER_INVOICE;
  }

  get isValidation() {
    return this.fields.type.value === INVOICE_TYPE_ENUM.VALIDATION_INVOICE;
  }

  get currentBalance() {
    return this.fields.line_items.value.reduce((agg, lineItem) => agg += lineItem.getCurrentBalance(), 0)
  }

  static createFromQueryValues(values) {
    const invoice = new this();

    for (let field in invoice.fields) {
      if (values.hasOwnProperty(field)) {
        switch (field) {
          case 'due_on':
          case 'created_at':
            invoice.fields[field].value = (values[field] === null) ?
              values[field] :
              new Date(Number(values[field]));
            break;
          case 'payor':
            invoice.fields[field].value = (values[field] === null) ?
              values[field] :
              Payor.createFromQueryValues(values[field]);
            break;
          case 'parkers':
            if (Array.isArray(values[field])) {
              for (let parker of values[field]) {
                invoice.fields[field].value.push(Parker.createFromQueryValues(parker));
              }
            }
            break;
          case 'line_items':
            if (Array.isArray(values[field])) {
              for (let invoiceLineItem of values[field]) {
                invoice.fields[field].value.push(InvoiceLineItem.createFromQueryValues(invoiceLineItem));
              }
            }
            break;
          case 'subscription':
            invoice.fields[field].value = (values[field] === null) ?
              values[field] :
              Subscription.createFromQueryValues(values[field]);
            break;
          case 'payments':
            if (Array.isArray(values[field])) {
              for (let payment of values[field]) {
                invoice.fields[field].value.push(Payment.createFromQueryValues(payment));
              }
            }
            break;
          case 'refunds':
            if (Array.isArray(values[field])) {
              for (let refund of values[field]) {
                invoice.fields[field].value.push(Refund.createFromQueryValues(refund));
              }
            }
            break;
          case 'transaction':
            invoice.fields[field].value = (values[field] === null) ?
            values[field] :
            Transaction.createFromQueryValues(values[field]);
            break;
          default:
            invoice.fields[field].value = values[field];
        }
      }
    }

    return invoice;
  }


  static getFieldAllowedValues(fields) {
    return Entity.getFieldAllowedValues(
      invoiceEntityFieldsWithStaticAllowedValues,
      invoiceEntityFieldsWithDynamicAllowedValues,
      fields
    );
  }


  getCreateQueryFieldValues() {
    const values = {};
    values.parking_period_month = this.fields.parking_period_month.value;
    values.parking_period_year = parseInt(this.fields.parking_period_year.value);
    values.subscription_id = this.fields.subscription_id.value;
    values.invoice_due_date = this.fields.due_on.value;
    values.type = this.fields.type.value;
    values.notes = this.fields.notes.value;
    values.detail_notes = this.fields.detail_notes.value;
    values.line_items = [];
    this.fields.line_items.value.forEach(lineItem => {
      const lineItemQueryValues = lineItem.getQueryFieldValues();
      if (lineItemQueryValues) values.line_items.push(lineItemQueryValues);
    });

    return values;
  }


  getUpdateQueryFieldValues() {
    const values = {};

    values.invoice_id = this.fields.id.value;
    values.invoice_type = this.fields.type.value;
    values.invoice_due_date = this.fields.due_on.value;
    values.notes = this.fields.notes.value;
    values.detail_notes = this.fields.detail_notes.value;
    values.line_items = this.fields.line_items.value.map(lineItem => {
      return lineItem.getQueryFieldValues();
    });
    values.transactions = this.getTransactionQueryFields();

    return values;
  }

  getTransactionQueryFields() {
    const queryFieldValues = this.fields.transaction.value?.getQueryFieldValues();
    const invoiceTransactionFields = queryFieldValues ? [ queryFieldValues ] : [];

    return [
      ...invoiceTransactionFields,
      ...this.fields.line_items.value.reduce((transactions, lineItem) => [
        ...transactions,
        ...lineItem.getTransactionQueryFields()
      ], [])
    ];
  }

  get reversablePayments() {
    return this.fields.payments.value.filter(payment => this.paymentIsReversable(payment));
  }

  paymentIsReversable(payment) {
    const paymentId = payment.fields.id.value;
    const reversableLineItems = this.fields.line_items.value.filter(lineItem => {
      const { reversablePaymentIds } = lineItem;

      return reversablePaymentIds.includes(paymentId);
    });

    return reversableLineItems.length > 0;
  }

  updateTransaction(transaction) {
    if(transaction.invoice_id) {
      this.fields.transaction.value.update(transaction);
    } else {
      const lineItem = this.fields.line_items.value.find(item => item.fields.id.value === transaction.line_item_id);
      lineItem.updateEntryTransaction(transaction);
    }
  }

  save(queryFields) {
    return new Promise((resolve, reject) => {
      const data = this.getUpdateQueryFieldValues();

      queryUpdateInvoice(data, queryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const invoice = Invoice.createFromQueryValues(response.data.updateInvoice);
        resolve(invoice);
      }, error => {
        reject([error]);
      });
    });
  }
}
